import { gql, useMutation, useQuery } from '@apollo/client';
import { Alert, Box, LinearProgress, Stack, Typography } from '@mui/material';
import { StatusBadge } from '../components/coreComponents/badge';
import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Button } from '../components/coreComponents/button';
import { SecretValue } from '../components/coreComponents/secretValue';
import { MainWrapper, MainWrapperHeader } from '../components/mainPageWrapper';

export function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data, loading, refetch } = useQuery(query, {
    variables: { id: id ?? '' },
    skip: id == null,
    fetchPolicy: 'cache-and-network'
  });

  const job = data?.sqsJobOne ?? null;
  const isInflight = job?.status === 'pending' || job?.status === 'processing';

  useEffect(() => {
    if (!isInflight) return;
    const interval = setInterval(() => {
      void refetch();
    }, 3000);
    return () => clearInterval(interval);
  }, [isInflight, refetch]);

  if (loading && job == null) {
    return (
      <MainWrapper>
        <MainWrapperHeader title="Job" onBack={() => navigate('/jobs')} />
        <Typography variant="body2">Loading…</Typography>
      </MainWrapper>
    );
  }

  if (job == null) {
    return (
      <MainWrapper>
        <MainWrapperHeader title="Job not found" onBack={() => navigate('/jobs')} />
      </MainWrapper>
    );
  }

  const isRestore = job.type === 'restoreAccount';
  const isCompleted = job.status === 'completed';
  const isFailed = job.status === 'failed';

  return (
    <MainWrapper>
      <MainWrapperHeader
        title={`Job — ${job.type}`}
        description={job.id}
        onBack={() => navigate('/jobs')}
        primaryAction={
          <Button variant="secondary" onClick={() => void refetch()}>
            Refresh
          </Button>
        }
      />

      <Stack spacing={4} sx={{ maxWidth: 720 }}>
        <Box>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="labelSmall">Status</Typography>
            <StatusChip status={job.status} />
          </Stack>
          <LinearProgress
            variant="determinate"
            value={job.progress}
            color={isFailed ? 'error' : isCompleted ? 'success' : 'primary'}
            sx={{ height: 8, borderRadius: 4 }}
          />
          <Typography
            variant="labelSmall"
            sx={{ color: 'text.secondary', mt: 1, display: 'block' }}
          >
            {job.progress}%
          </Typography>
        </Box>

        {isRestore && job.accountRestore?.temporaryPassword != null && (
          <RestoreTemporaryPasswordAlert
            jobId={job.id}
            ownerEmail={job.accountRestore.ownerEmail}
            password={job.accountRestore.temporaryPassword}
            onDismissed={() => void refetch()}
          />
        )}

        {isRestore &&
          job.accountRestore?.linkedExistingUser === true &&
          job.accountRestore.temporaryPassword == null && (
            <Alert severity="info">
              <strong>{job.accountRestore.ownerEmail}</strong> already had credentials in another
              tenant — they were linked into the restored account without a temporary password. They
              can sign in with their existing password.
            </Alert>
          )}

        {isFailed && job.errorMessage != null && (
          <Alert severity="error" sx={{ overflowWrap: 'anywhere' }}>
            {job.errorMessage}
          </Alert>
        )}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '180px 1fr',
            rowGap: 2,
            columnGap: 3
          }}
        >
          <Label>Job ID</Label>
          <Value monospace>{job.id}</Value>

          <Label>Type</Label>
          <Value>{job.type}</Value>

          {job.accountProvision != null && (
            <>
              <Label>Owner</Label>
              <Value>
                {`${job.accountProvision.ownerFirstName} ${job.accountProvision.ownerLastName}`.trim()}
              </Value>

              <Label>Owner email</Label>
              <Value>{job.accountProvision.ownerEmail}</Value>

              <Label>Company</Label>
              <Value>{job.accountProvision.organisationName}</Value>

              <Label>Account ID</Label>
              <Value
                monospace={job.accountProvision.accountId != null}
                subtle={job.accountProvision.accountId == null}
              >
                {job.accountProvision.accountId ?? 'Creating…'}
              </Value>
            </>
          )}

          {job.accountDeletion != null && (
            <>
              <Label>Account alias</Label>
              <Value subtle={job.accountDeletion.accountAlias == null}>
                {job.accountDeletion.accountAlias ?? '—'}
              </Value>

              <Label>Owner email</Label>
              <Value subtle={job.accountDeletion.ownerEmail == null}>
                {job.accountDeletion.ownerEmail ?? '—'}
              </Value>

              <Label>Account ID</Label>
              <Value monospace>{job.accountDeletion.accountId}</Value>

              <Label>Users at deletion</Label>
              <Value>{String(job.accountDeletion.userCount)}</Value>
            </>
          )}

          {job.accountRestore != null && (
            <>
              <Label>Account alias</Label>
              <Value subtle={job.accountRestore.accountAlias == null}>
                {job.accountRestore.accountAlias ?? '—'}
              </Value>

              <Label>Owner email</Label>
              <Value>{job.accountRestore.ownerEmail}</Value>

              <Label>Account ID</Label>
              <Value monospace>{job.accountRestore.accountId}</Value>

              {job.accountRestore.buildLogsUrl != null && (
                <>
                  <Label>Build logs</Label>
                  <Value>
                    <Box
                      component="a"
                      href={job.accountRestore.buildLogsUrl}
                      target="_blank"
                      rel="noreferrer"
                      sx={{ color: 'primary.main', textDecoration: 'underline' }}
                    >
                      Open in CodeBuild console
                    </Box>
                  </Value>
                </>
              )}
            </>
          )}

          <Label>Created at</Label>
          <Value>{new Date(job.createdAt).toLocaleString()}</Value>

          <Label>Completed at</Label>
          <Value subtle={job.completedAt == null}>
            {job.completedAt != null ? new Date(job.completedAt).toLocaleString() : '—'}
          </Value>

          <Label>Created by</Label>
          <Value monospace={job.createdBy != null} subtle={job.createdBy == null}>
            {job.createdBy ?? '—'}
          </Value>
        </Box>
      </Stack>
    </MainWrapper>
  );
}

function RestoreTemporaryPasswordAlert(props: {
  readonly jobId: string;
  readonly ownerEmail: string;
  readonly password: string;
  readonly onDismissed: () => void;
}) {
  const [dismiss, { loading: dismissing }] = useMutation(dismissPasswordMutation);

  return (
    <Alert severity="warning" sx={{ maxWidth: 720 }}>
      <Stack spacing={2}>
        <Typography variant="body2">
          Temporary password for <strong>{props.ownerEmail}</strong>. Share it with the owner so
          they can log into the restored account, then dismiss it.
        </Typography>
        <SecretValue value={props.password} />
        <Stack direction="row" spacing={2}>
          <Button
            variant="primary"
            disabled={dismissing}
            onClick={async () => {
              await dismiss({ variables: { id: props.jobId } });
              props.onDismissed();
            }}
          >
            {dismissing ? 'Dismissing…' : 'Dismiss'}
          </Button>
        </Stack>
      </Stack>
    </Alert>
  );
}

function Label({ children }: { readonly children: React.ReactNode }) {
  return (
    <Typography variant="labelSmall" sx={{ color: 'text.secondary' }}>
      {children}
    </Typography>
  );
}

interface ValueProps {
  readonly children: React.ReactNode;
  readonly monospace?: boolean;
  readonly subtle?: boolean;
}

function Value({ children, monospace, subtle }: ValueProps) {
  return (
    <Typography
      variant="body2"
      sx={{
        fontFamily: monospace === true ? 'monospace' : undefined,
        color: subtle === true ? 'text.secondary' : undefined,
        overflowWrap: 'anywhere'
      }}
    >
      {children}
    </Typography>
  );
}

function StatusChip({ status }: { readonly status: string }) {
  const color =
    status === 'completed'
      ? 'success'
      : status === 'failed'
        ? 'error'
        : status === 'processing'
          ? 'info'
          : 'default';
  return <StatusBadge tone={color} label={status} />;
}

const query = gql<JobDetailGQL>`
  query JobDetail($id: ID!) {
    sqsJobOne(id: $id) {
      id
      type
      status
      progress
      errorMessage
      createdBy
      createdAt
      completedAt
      accountProvision {
        ownerEmail
        ownerFirstName
        ownerLastName
        organisationName
        accountId
      }
      accountDeletion {
        accountId
        accountAlias
        ownerEmail
        userCount
      }
      accountRestore {
        accountId
        accountAlias
        ownerEmail
        temporaryPassword
        linkedExistingUser
        buildLogsUrl
      }
    }
  }
`;

const dismissPasswordMutation = gql<JobDetailDismissPasswordGQL>`
  mutation JobDetailDismissPassword($id: ID!) {
    sqsJobDismissPassword(id: $id) {
      id
      accountRestore {
        temporaryPassword
      }
    }
  }
`;
